import React, { useCallback, useEffect, useMemo, useState } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import {
  ChevronDown,
  ChevronRight,
  CircleDot,
} from "lucide-react";

import { CurrentStageStep, Stagestep } from "../core/_models";
import { useLeftStepper } from "../../../../_theme/context/LeftStepperContext";
import { HideStepperButton } from "../components/HideStepperButton";

interface StepSidebarProps {
  steps: Stagestep[];
  currentStep: CurrentStageStep;
  onStepClick: (key: string) => void;
}

const StepIconSidebar: React.FC<StepSidebarProps> = ({
  steps,
  currentStep,
  onStepClick,
}) => {
  const { leftStepperHide } = useLeftStepper();

  const currentStepKey = currentStep.step.key;

  const [expandedMenus, setExpandedMenus] = useState<
    Record<string, boolean>
  >({});

  /**
   * Find all parent keys that contain current active submenu
   */
  const activeParentKeys = useMemo(() => {
    const parentKeys = new Set<string>();

    steps.forEach((step) => {
      const isParentActive = step.submenu?.some(
        (submenu) => submenu.key === currentStepKey
      );

      if (isParentActive) {
        parentKeys.add(step.key);
      }
    });

    return parentKeys;
  }, [steps, currentStepKey]);

  /**
   * Auto expand current active parent menu
   */
  useEffect(() => {
    const parent = steps.find((step) =>
      step.submenu?.some(
        (submenu) => submenu.key === currentStepKey
      )
    );

    if (!parent) return;

    setExpandedMenus((prev) => {
      if (prev[parent.key]) return prev;

      return {
        ...prev,
        [parent.key]: true,
      };
    });
  }, [steps, currentStepKey]);

  /**
   * Toggle submenu
   */
  const toggleMenu = useCallback((key: string) => {
    setExpandedMenus((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  }, []);

  /**
   * Step click handler
   */
  const handleStepClick = useCallback(
    (key: string) => {
      onStepClick(key);
    },
    [onStepClick]
  );

  return (
    <div
      className={`card position-relative d-flex overflow-visible justify-content-center justify-content-xl-start flex-row-auto ${leftStepperHide
          ? "w-100px"
          : "w-xl-300px w-xxl-350px w-100"
        } me-5`}
    >
      <div className="card-body p-0 w-100">
        <div className="hide-steps-btn">
          <HideStepperButton />
        </div>

        <div className="stepper-nav w-100 overflow-y-auto h-100 pb-3">
          {steps.map((step) => {
            const hasSubmenu = Boolean(step.submenu?.length);

            const isExpanded = Boolean(
              expandedMenus[step.key]
            );

            const isCurrentStep =
              step.key === currentStepKey ||
              activeParentKeys.has(step.key);

            return (
              <React.Fragment key={step.key}>
                {/* Main Step */}
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() =>
                    handleStepClick(step.key)
                  }
                  onKeyDown={(e) => {
                    if (
                      e.key === "Enter" ||
                      e.key === " "
                    ) {
                      handleStepClick(step.key);
                    }
                  }}
                  className={`stepper-item cursor-pointer w-100 px-4 py-1 mt-4 ${isCurrentStep
                      ? "current active-step"
                      : ""
                    }`}
                  data-kt-stepper-element="nav"
                >
                  <div className="stepper-wrapper">
                    {/* Step Icon: the title shows as a tooltip only while the sidebar is collapsed.
                        OverlayTrigger passes positioning props to its overlay, so it always gets a Tooltip. */}
                    {leftStepperHide ? (
                      <OverlayTrigger
                        placement="auto"
                        overlay={
                          <Tooltip id={`tooltip-${step.key}`}>
                            {step.title}
                          </Tooltip>
                        }
                      >
                        <div className={`stepper-icon w-40px h-40px mx-0 ${step.iconColor} ${step.iconBackground}`}>
                          {step.icon}
                        </div>
                      </OverlayTrigger>
                    ) : (
                      <div className={`stepper-icon w-40px h-40px ${step.iconColor} ${step.iconBackground}`}>
                        {step.icon}
                      </div>
                    )}

                    {/* Step Label */}
                    <div className="stepper-label w-100">
                      <h3
                        className={`stepper-title d-flex justify-content-between align-items-center ${isCurrentStep
                            ? "text-primary"
                            : "text-gray-700"
                          }`}
                      >
                        {leftStepperHide ? "" : <span>{step.title}</span>}

                        {hasSubmenu && (
                          <button
                            type="button"
                            className="btn btn-sm btn-icon"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleMenu(step.key);
                            }}
                          >
                            {isExpanded ? (
                              <ChevronDown size={18} />
                            ) : (
                              <ChevronRight size={18} />
                            )}
                          </button>
                        )}
                      </h3>
                    </div>
                  </div>
                </div>

                {/* Submenu */}
                {hasSubmenu && isExpanded && (
                  <div className="ms-18 mb-0">
                    {step.submenu?.map((submenu) => {
                      const isCurrentSubStep =
                        submenu.key === currentStepKey;

                      return (
                        <div
                          key={submenu.key}
                          role="button"
                          tabIndex={0}
                          onClick={() =>
                            handleStepClick(
                              submenu.key
                            )
                          }
                          onKeyDown={(e) => {
                            if (
                              e.key === "Enter" ||
                              e.key === " "
                            ) {
                              handleStepClick(
                                submenu.key
                              );
                            }
                          }}
                          className={`stepper-item cursor-pointer mt-3 ${isCurrentSubStep
                              ? "current"
                              : ""
                            }`}
                          data-kt-stepper-element="nav"
                        >
                          <div className="stepper-wrapper">
                            <div className="stepper-icon w-30px h-30px bg-light-primary">
                              {submenu.icon || (
                                <CircleDot size={18} />
                              )}
                            </div>

                            <div className="stepper-label">
                              <h3
                                className={`stepper-title fs-5 ${isCurrentSubStep
                                    ? "text-primary"
                                    : "text-gray-700"
                                  }`}
                              >
                                {submenu.title}
                              </h3>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default React.memo(StepIconSidebar);